import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Container,
  Divider,
  Paper,
  Title,
  Text,
  PasswordInput,
  Button,
  Stack,
  Loader,
  Center,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconLock, IconArrowRight } from "@tabler/icons-react";
import { PerformRequest } from "../../hooks/usePerformRequest";
import { Endpoints } from "../../lib/endpoints";
import { HttpCodes } from "../../lib/data";

import "../Login/styles.css";

const Onboarding = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [validating, setValidating] = useState(true);
  const [isValid, setIsValid] = useState(false);
  const [adminEmail, setAdminEmail] = useState("");
  const [adminName, setAdminName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.title = "Onboarding | Virel Admin";
    const validateToken = async () => {
      if (!token) {
        setValidating(false);
        return;
      }

      const endpoint = Endpoints.ValidateOnboarding(token);
      const response = await PerformRequest({
        method: endpoint.method,
        route: endpoint.route,
      });

      if (response.status === HttpCodes.Success) {
        const data = response.data as { email: string; name?: string };
        setIsValid(true);
        setAdminEmail(data.email);
        setAdminName(data.name || "");
      } else {
        setIsValid(false);
        setError(response.message || "Invalid or expired invitation link.");
      }
      setValidating(false);
    };

    validateToken();
  }, [token]);

  const form = useForm({
    initialValues: {
      password: "",
      confirmPassword: "",
    },
    validate: {
      password: (value: string) =>
        value.length < 8 ? "Password must be at least 8 characters" : null,
      confirmPassword: (value: string, values: { password: string }) =>
        value !== values.password ? "Passwords do not match" : null,
    },
  });

  const handleSubmit = async (values: typeof form.values) => {
    if (!token) return;

    setSubmitting(true);
    setError(null);

    const endpoint = Endpoints.CompleteOnboarding(token);
    const response = await PerformRequest({
      method: endpoint.method,
      route: endpoint.route,
      data: {
        password: values.password,
      },
    });

    setSubmitting(false);

    if (response.status === HttpCodes.Success) {
      setTimeout(() => {
        navigate("/login");
      }, 2000);
    } else {
      setError(response.message || "Failed to complete onboarding.");
    }
  };

  if (validating) {
    return (
      <div className="admin-login">
        <div className="login-background">
          <div className="login-orb orb-1"></div>
          <div className="login-orb orb-2"></div>
        </div>
        <Center style={{ height: "100vh" }}>
          <Loader size="xl" color="brand" />
        </Center>
      </div>
    );
  }

  if (!isValid) {
    return (
      <div className="admin-login">
        <div className="login-background">
          <div className="login-orb orb-1"></div>
          <div className="login-orb orb-2"></div>
        </div>
        <Container size="sm" className="login-container">
          <Paper className="login-card" shadow="xl">
            <Stack align="center" gap="md">
              <Title order={2} className="login-title">
                Invalid Invitation
              </Title>
              <Text c="dimmed" ta="center">
                {error ||
                  "This invitation link is invalid or has expired. Please ask your administrator for a new invite."}
              </Text>
              <Button onClick={() => navigate("/login")}>Go to Login</Button>
            </Stack>
          </Paper>
        </Container>
      </div>
    );
  }

  return (
    <div className="admin-login">
      <div className="login-background">
        <div className="login-orb orb-1"></div>
        <div className="login-orb orb-2"></div>
        <div className="login-orb orb-3"></div>
        <div className="login-grid"></div>
      </div>

      <Container
        size="sm"
        className="login-container"
        style={{ maxWidth: 480 }}
      >
        <div className="login-brand" style={{ marginBottom: 24, padding: 24 }}>
          <div className="login-brand-top" style={{ justifyContent: "center" }}>
            <div className="login-logo">
              <img src="/icon.png" alt="Virel Logo" style={{ height: 40 }} />
            </div>
            <Title order={1} className="login-brand-title">
              Virel Admin
            </Title>
          </div>
        </div>

        <Paper className="login-card" shadow="xl">
          <Stack gap="lg" className="login-content">
            <div className="login-header">
              <Title order={2} className="login-title">
                Set Your Password
              </Title>
              <Text size="sm" c="dimmed" className="login-subtitle">
                Welcome {adminName || adminEmail}! Please set a secure password
                to activate your account.
              </Text>
            </div>

            <Divider className="login-divider" />

            <form onSubmit={form.onSubmit(handleSubmit)}>
              <Stack gap="md">
                <PasswordInput
                  placeholder="Create a password"
                  label="New Password"
                  size="md"
                  leftSection={<IconLock size={18} />}
                  classNames={{
                    input: "login-input",
                    label: "login-label",
                  }}
                  {...form.getInputProps("password")}
                />

                <PasswordInput
                  placeholder="Confirm your password"
                  label="Confirm Password"
                  size="md"
                  leftSection={<IconLock size={18} />}
                  classNames={{
                    input: "login-input",
                    label: "login-label",
                  }}
                  {...form.getInputProps("confirmPassword")}
                />

                {error && (
                  <Alert color="red" variant="light" className="login-error">
                    {error}
                  </Alert>
                )}

                <Button
                  type="submit"
                  size="md"
                  fullWidth
                  loading={submitting}
                  rightSection={<IconArrowRight size={18} />}
                  className="login-button"
                >
                  Activate Account
                </Button>
              </Stack>
            </form>
          </Stack>
        </Paper>
      </Container>
    </div>
  );
};

export default Onboarding;
